import React, { FC, RefObject, useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import { Button, Typography, AlertBar } from '@packages/ui/shared';
import { isEvBatteryScan } from '@web/utils/is-ev-battery-scan';
import { VIN } from '../types';
import { analyticsService, vinSessionService } from '@web/services/singletons';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { useAuthState } from '@packages/contexts/auth';
import { useTranslation } from 'react-i18next';
import BinUpdateFormWizard from './bin-update-flow/bin-update-form-wizard';
import { useAbortController } from '@packages/core/http';
import { toBoolean } from '../utils/vinSessionUtils';

type VinSessionDropdownProps = {
    vinData: VIN | undefined;
    dropDownRef: RefObject<HTMLDivElement>;
    showHideBinUpdateCallback: (showing: boolean) => void;
};

const useStyles = createUseStyles({
    tableContainer: {
        position: 'fixed',
        top: 154,
        maxHeight: '67%',
        width: '100%',
        backgroundColor: colors.white,
        zIndex: 10,
        padding: 16,
        boxShadow: '0px 2px 8px 0px #00000029',
    },
    row1: {
        width: '50%',
        backgroundColor: `#F8F8F8 !important`,
        border: 'solid 1px #E3E3E3',
        padding: '10px !important',
    },
    row2: {
        width: '50%',
        backgroundColor: '#FFFFFF',
        border: 'solid 1px #E3E3E3',
        padding: '10px !important',
    },
    heading: {
        height: 40,
        border: 'solid 1px #E3E3E3',
        backgroundColor: '#DEE6EF',
        padding: '10px !important',
    },
    link: {
        fontWeight: '400',
        fontSize: '15px',
        lineHeight: '24px',
        marginRight: 16,
    },
    binContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    binButtonContainer: {
        display: 'inline-flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexGrow: '1',
    },
    tooltip: {
        marginTop: 5,
        '& .tooltip-inner': {
            backgroundColor: colors.graySeven,
            borderRadius: 4,
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '20px',
            padding: '3px 8px',
            textAlign: 'left',
        },
    },
});

const VinSessionDropdown: FC<VinSessionDropdownProps> = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { abortSignalRef } = useAbortController();
    const { vinData, dropDownRef } = props;
    const updateBatteryStatusEnabledFuel = ['Electric', 'Hybrid', 'Plug-In Hybrid'];
    const [updateBinWizardOpen, setUpdateBinWizardOpen] = useState<boolean>(false);
    const [showBanner, setShowBanner] = useState<boolean>(false);
    const [latestBinInformationFromApi, setLatestBinInformationFromApi] = useState<undefined | any>();
    const { account } = useAuthState();
    const alertBarRef = useRef<HTMLDivElement>(null);
    const [top, setTop] = useState(156);

    const getLatestBinInfromation = async (vin: string): Promise<void> => {
        const response = await vinSessionService.getVinBinDetails({
            vin,
            signal: abortSignalRef.current,
            ignoreCache: true,
        });
        const data = response?.data;
        if (Array.isArray(data) && data[0] && 'evBattery' in data[0]) {
            setLatestBinInformationFromApi(data[0].evBattery);
        }
    };

    useEffect(() => {
        if (showBanner && alertBarRef?.current?.offsetHeight) {
            setTop(156 + alertBarRef?.current?.offsetHeight);
            setTimeout(() => {
                setShowBanner(false);
                setUpdateBinWizardOpen(false);
            }, 5000);
        } else {
            setTop(156);
        }
    }, [alertBarRef, showBanner]);
    return (
        <>
            <div ref={alertBarRef}>
                <AlertBar
                    success
                    show={showBanner}
                    onClose={() => {
                        setShowBanner(false);
                        props.showHideBinUpdateCallback(false);
                    }}
                >
                    <Typography>
                        {t(
                            'Battery information submitted successfully. Updated BIN details may take up to 15 minutes to appear.'
                        )}
                    </Typography>
                </AlertBar>
            </div>
            <div className={classes.tableContainer} ref={dropDownRef} style={{ top: top }}>
                <BinUpdateFormWizard
                    open={updateBinWizardOpen}
                    closeCallback={(result) => {
                        if (result === 'cancel') {
                            setUpdateBinWizardOpen(false);
                            props.showHideBinUpdateCallback(false);
                        } else if (result?.data instanceof Error || result.success === false) {
                            alert('Error');
                            //to-do error
                            //setUpdateBinWizardOpen(false);
                            props.showHideBinUpdateCallback(false);
                        } else {
                            //success
                            if (vinData?.vinRecord.vin) getLatestBinInfromation(vinData.vinRecord.vin);
                            setShowBanner(true);
                        }
                    }}
                    existingBin={latestBinInformationFromApi?.bin || vinData?.vinRecord.bin}
                />
                <Table>
                    <tbody>
                        <tr>
                            <td className={classes.row1}>
                                <Typography variant="caption4">Carline: </Typography>
                                <Typography
                                    variant="caption4"
                                    style={{ fontWeight: '400', textTransform: 'capitalize' }}
                                >
                                    {vinData?.vinRecord?.model}
                                </Typography>
                            </td>
                            <td className={classes.row2}>
                                <Typography variant="caption4">Year: </Typography>
                                <Typography
                                    variant="caption4"
                                    style={{ fontWeight: '400', textTransform: 'capitalize' }}
                                >
                                    {vinData?.vinRecord?.modelYear}
                                </Typography>
                            </td>
                        </tr>
                        <tr>
                            <td className={classes.row2}>
                                <Typography variant="caption4">Engine: </Typography>
                                <Typography
                                    variant="caption4"
                                    style={{ fontWeight: '400', textTransform: 'capitalize' }}
                                >
                                    {(() => {
                                        const r = vinData?.vinRecord;
                                        if (!r?.engineSize && !r?.engine && !r?.fuel) return '—';
                                        const size = r?.engineSize != null ? `${r.engineSize} L` : '';
                                        const turbo = r?.turboIndicator === 'true' ? ' Turbo' : '';
                                        const cyl = r?.engine != null ? ` ${r.engine} Cylinders` : '';
                                        const fuel = r?.fuel != null ? ` ${r.fuel}` : '';
                                        return `${size}${turbo}${cyl}${fuel}`.trim() || '—';
                                    })()}
                                </Typography>
                            </td>
                            <td className={classes.row1}>
                                <Typography variant="caption4">Engine n°: </Typography>
                                <Typography
                                    variant="caption4"
                                    style={{ fontWeight: '400', textTransform: 'capitalize' }}
                                >
                                    {vinData?.vinRecord?.engineId}
                                </Typography>
                            </td>
                        </tr>
                        <tr>
                            <td className={classes.row1}>
                                <Typography variant="caption4">Trim: </Typography>
                                <Typography
                                    variant="caption4"
                                    style={{ fontWeight: '400', textTransform: 'capitalize' }}
                                >
                                    {vinData?.vinRecord?.trimDescription}
                                </Typography>
                            </td>
                            <td className={classes.row2}>
                                <Typography variant="caption4">Model: </Typography>
                                <Typography
                                    variant="caption4"
                                    style={{ fontWeight: '400', textTransform: 'capitalize' }}
                                >
                                    {vinData?.vinRecord?.modelNumber}
                                </Typography>
                            </td>
                        </tr>
                        <tr>
                            <td className={classes.row2}>
                                <Typography variant="caption4">Transmission Type: </Typography>
                                <Typography
                                    variant="caption4"
                                    style={{ fontWeight: '400', textTransform: 'capitalize' }}
                                >
                                    {vinData?.vinRecord?.driveTrain} {vinData?.vinRecord?.transmissionType}
                                </Typography>
                            </td>
                            <td className={classes.row1}>
                                <Typography variant="caption4">Transmission n°: </Typography>
                                <Typography
                                    variant="caption4"
                                    style={{ fontWeight: '400', textTransform: 'capitalize' }}
                                >
                                    {vinData?.vinRecord?.vehicleTransmissionNumber}
                                </Typography>
                            </td>
                        </tr>
                        <tr>
                            <td className={classes.row1}>
                                <Typography variant="caption4">Fuel Type: </Typography>
                                <Typography
                                    variant="caption4"
                                    style={{ fontWeight: '400', textTransform: 'capitalize' }}
                                >
                                    {vinData?.vinRecord?.fuel}
                                </Typography>
                            </td>
                            <td className={classes.row2}>
                                <Typography variant="caption4">Exterior Color: </Typography>
                                <Typography
                                    variant="caption4"
                                    style={{ fontWeight: '400', textTransform: 'capitalize' }}
                                >
                                    {vinData?.vinRecord?.colorDescription}
                                </Typography>
                            </td>
                        </tr>
                        <tr>
                            <td className={classes.row2}>
                                <Typography variant="caption4">Turbo Indicator: </Typography>
                                <Typography
                                    variant="caption4"
                                    style={{ fontWeight: '400', textTransform: 'capitalize' }}
                                >
                                    {toBoolean(vinData?.vinRecord?.turboIndicator) ? 'Yes' : 'No'}
                                </Typography>
                            </td>
                            <td className={classes.row1}>
                                {isEvBatteryScan() && (
                                    <span className={classes.binContainer}>
                                        <Typography variant="caption4">Battery BIN: </Typography>
                                        <span className={classes.binButtonContainer}>
                                            <Typography
                                                variant="caption4"
                                                style={{ fontWeight: '400', textTransform: 'capitalize' }}
                                            >
                                                {latestBinInformationFromApi?.bin || vinData?.vinRecord.bin}
                                            </Typography>
                                            <span>
                                                {updateBatteryStatusEnabledFuel.includes(
                                                    vinData?.vinRecord.fuel || ''
                                                ) ? (
                                                    <Button
                                                        variant="ghost-blue"
                                                        onPress={(e) => {
                                                            analyticsService.logEvent(
                                                                ANALYTICS_EVENTS.EVD_START_CLICKED,
                                                                {
                                                                    vin: vinData?.vinRecord.vin,
                                                                    userId: account?.accountId,
                                                                    source: 'vin_session',
                                                                }
                                                            );
                                                            setUpdateBinWizardOpen(true);
                                                            props.showHideBinUpdateCallback(true);
                                                        }}
                                                    >
                                                        Update EV Battery
                                                    </Button>
                                                ) : (
                                                    <OverlayTrigger
                                                        placement="top"
                                                        delay={{ show: 250, hide: 400 }}
                                                        overlay={
                                                            <Tooltip
                                                                id={'1'}
                                                                style={{
                                                                    fontSize: 16,
                                                                    padding: '8px 12px',
                                                                    textAlign: 'center',
                                                                }}
                                                            >
                                                                {'Feature not available'}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span>
                                                            <Button
                                                                variant="ghost-blue"
                                                                style={{ pointerEvents: 'none' }}
                                                                disabled
                                                            >
                                                                {'Update EV Battery'}
                                                            </Button>
                                                        </span>
                                                    </OverlayTrigger>
                                                )}
                                            </span>
                                        </span>
                                    </span>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </div>
        </>
    );
};

export default VinSessionDropdown;
